import { useCallback, useEffect, useState } from "react";
import { CalendarHeart, MapPin, Clock, Plus, Pencil, Trash2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { t, getLang } from "@/lib/i18n";
import { EventDialog } from "@/components/EventDialog";
import { toast } from "sonner";

const STATUSES = [["going", "Going", "bg-[#2E7D32]"], ["maybe", "Maybe", "bg-[#C59B27]"], ["no", "Can't come", "bg-[#7E8E85]"]];
const todayStr = () => new Date().toLocaleDateString("en-CA");

const Rsvp = ({ e, onDone }) => {
  const { user, login } = useAuth();
  const [count, setCount] = useState(e.my_rsvp?.count || 1);
  if (!user) return <Button variant="outline" className="rounded-full" onClick={login} data-testid={`event-signin-${e.event_id}`}>{t("Sign in to reply")}</Button>;
  const send = async (status) => {
    try { await api.post(`/events/${e.event_id}/rsvp`, { status, count: Number(count) || 1 }); onDone(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`event-rsvp-${e.event_id}`}>
      <span className="text-xs text-[#7E8E85] mr-1">{t("Your reply")}:</span>
      {STATUSES.map(([k, label, bg]) => (
        <button key={k} onClick={() => send(k)} data-testid={`rsvp-${k}-${e.event_id}`}
          className={`px-4 h-9 rounded-full text-sm border transition-colors ${e.my_rsvp?.status === k ? `${bg} text-white border-transparent` : "bg-white border-[#E5DEC9] hover:border-[#1E3A2B]"}`}>{t(label)}</button>
      ))}
      <label className="flex items-center gap-1.5 text-xs text-[#52635B] ml-2">{t("How many people?")}
        <input type="number" min={1} max={50} value={count} onChange={(ev) => setCount(ev.target.value)} className="w-14 h-9 rounded-lg border border-[#E5DEC9] px-2" data-testid={`rsvp-count-${e.event_id}`} />
      </label>
    </div>
  );
};

const EventCard = ({ e, onChange, onEdit, past }) => {
  const { user } = useAuth();
  const d = new Date(e.date + "T00:00:00");
  const locale = getLang() === "hi" ? "hi-IN" : undefined;
  const canEdit = user?.is_admin || user?.user_id === e.created_by;
  const del = async () => {
    if (!window.confirm(`${t("Delete")}: ${e.title}?`)) return;
    try { await api.delete(`/events/${e.event_id}`); onChange(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <article className={`rounded-3xl border border-[#E5DEC9] bg-white p-6 flex flex-col md:flex-row gap-6 ${past ? "opacity-70" : ""}`} data-testid={`event-card-${e.event_id}`}>
      <div className="w-20 shrink-0 text-center rounded-2xl bg-[#1E3A2B] text-[#FDFBF7] py-3 h-fit">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em]">{d.toLocaleDateString(locale, { month: "short" })}</p>
        <p className="font-serif text-3xl font-bold">{d.getDate()}</p>
        <p className="font-mono text-[10px]">{d.getFullYear()}</p>
      </div>
      <div className="flex-1 min-w-0 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] px-2.5 py-1 rounded-full bg-[#F4EDE2] text-[#D96B43]">{t(e.kind)}</span>
            <h3 className="font-serif text-2xl font-semibold mt-2" data-testid={`event-title-${e.event_id}`}>{e.title}</h3>
          </div>
          {canEdit && (
            <div className="flex">
              <Button size="icon" variant="ghost" onClick={() => onEdit(e)} data-testid={`event-edit-${e.event_id}`}><Pencil className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" className="text-[#C62828]" onClick={del} data-testid={`event-delete-${e.event_id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-4 text-sm text-[#52635B]">
          {e.time && <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{e.time}</span>}
          {e.location && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{e.location}</span>}
          <span className="flex items-center gap-1.5" data-testid={`event-going-count-${e.event_id}`}><Users className="w-4 h-4" />{e.going_count} {t("Going")}</span>
          <span className="text-[#7E8E85]">{t("Hosted by")} {e.host}</span>
        </div>
        {e.description && <p className="text-[#3A4741] whitespace-pre-line">{e.description}</p>}
        <div className="grid sm:grid-cols-3 gap-3">
          {STATUSES.map(([k, label]) => (
            <div key={k} className="text-sm" data-testid={`event-${k}-list-${e.event_id}`}>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#7E8E85] mb-1">{t(label)} ({e.rsvps[k].length})</p>
              <p className="text-[#52635B]">{e.rsvps[k].map((r) => `${r.name}${r.count > 1 ? ` +${r.count - 1}` : ""}`).join(", ") || "—"}</p>
            </div>
          ))}
        </div>
        {!past && <Rsvp e={e} onDone={onChange} />}
      </div>
    </article>
  );
};

export default function Events() {
  const { user } = useAuth();
  const [events, setEvents] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/events").then(({ data }) => setEvents(data)), []);
  useEffect(() => { load(); }, [load, user]);
  const today = todayStr();
  const upcoming = (events || []).filter((e) => e.date >= today);
  const past = (events || []).filter((e) => e.date < today).reverse();

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-14 space-y-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D96B43] mb-3 flex items-center gap-1.5"><CalendarHeart className="w-3.5 h-3.5" />{t("Family events")}</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight">{t("Weddings, pujas & reunions")}</h1>
          <p className="mt-3 text-[#52635B]">{t("See who's coming and let the family know if you'll be there.")}</p>
        </div>
        {user?.can_vote && <Button onClick={() => setEditing({})} className="rounded-full bg-[#D96B43] hover:bg-[#C05731] h-12 px-6" data-testid="new-event-btn"><Plus className="w-4 h-4 mr-2" />{t("New event")}</Button>}
      </div>
      {!user?.can_vote && <p className="text-sm text-[#7E8E85]">{t("Approved family members can create events.")}</p>}
      {events === null ? <div className="h-48 rounded-3xl bg-[#F4EDE2] animate-pulse" /> : (
        <>
          <section className="space-y-4" data-testid="events-upcoming">
            <h2 className="font-serif text-2xl font-semibold">{t("Upcoming")}</h2>
            {upcoming.length === 0 && <div className="rounded-3xl border border-dashed border-[#CDBF9F] p-12 text-center text-[#7E8E85]" data-testid="events-empty">{t("No upcoming events.")}</div>}
            {upcoming.map((e) => <EventCard key={e.event_id} e={e} onChange={load} onEdit={setEditing} />)}
          </section>
          {past.length > 0 && (
            <section className="space-y-4" data-testid="events-past">
              <h2 className="font-serif text-2xl font-semibold">{t("Past")}</h2>
              {past.map((e) => <EventCard key={e.event_id} e={e} onChange={load} onEdit={setEditing} past />)}
            </section>
          )}
        </>
      )}
      {editing && <EventDialog event={editing.event_id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
